/**
 * Legend for the calendar chips: what the fill / left bar colour, the border
 * style, the second-line tags and the flag dots mean. Rendered under both
 * closing calendars so the encoding is never a guess. Every swatch is built
 * from the same tokens the chips use (closingReadinessUtils), so it can't drift.
 */
import { useState } from "react";

import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { amber, slate, status } from "../../theme/colors";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { READINESS, READINESS_ORDER, SOFT_LINE } from "./closingReadinessUtils";
import { TYPE } from "./typography";

// The legend's swatch frame: one chip-sized box per row, always the same
// size so the labels line up. The frame border is the user-chosen hairline —
// a solid line, never dashed, so a dashed border can only ever mean "expected
// date" (the one row that draws it).
const FRAME_LINE = "#e3e8ee";
const FRAME_W = 34;
const FRAME_H = 18;

// A chip-sized frame whose only colour is the edge bar — how a readiness level
// actually shows on most chips (the fill is usually white: see Fill · date).
const BarSwatch = ({ c }) => (
  <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: c }} />
);

// The fill / border treatments (date confidence), drawn in a neutral colour so
// they read as a separate axis from the level colour.
const FILL_SX = {
  firm: { background: alpha(slate[500], 0.11), border: `1.5px solid ${alpha(slate[500], 0.25)}` },
  soft: { background: "#fff", border: `1.5px dashed ${SOFT_LINE}` },
  closed: { background: slate[50], border: `1.5px solid ${slate[200]}`, opacity: 0.75 },
};

const Dot = ({ c }) => (
  <Box sx={{ width: 7, height: 7, borderRadius: "99px", background: c, flexShrink: 0 }} />
);

// A text mark as the chip prints it (TR, z, the EV id, the day header).
const Mark = ({ children, sx }) => (
  <Txt sx={{ ...TYPE.microMono, fontWeight: 700, letterSpacing: "0.04em", color: slate[400], whiteSpace: "nowrap", ...sx }}>
    {children}
  </Txt>
);

// Swatch column is fixed so the labels line up; the hint wraps under itself
// rather than running off the edge of a narrow popover.
// Admin-only (`dev`): the rule behind a legend row, shown as a tooltip on the swatch
// (the first column). `logic` is { when, rules[], source? } — `when` is the
// one-line condition, `rules` the clauses that satisfy it, `source` where the
// value is computed (backend field / FE helper).
const LogicTip = ({ logic }) => (
  <Box sx={{ p: 0.5, maxWidth: 420 }}>
    <Txt sx={{ ...TYPE.micro, fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: "rgba(255,255,255,.6)", display: "block", mb: 0.5 }}>
      When it applies
    </Txt>
    <Txt sx={{ ...TYPE.meta, fontSize: 12, color: "#fff", display: "block", lineHeight: 1.45 }}>{logic.when}</Txt>
    {Array.isArray(logic.rules) && logic.rules.length > 0 && (
      <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2, display: "flex", flexDirection: "column", gap: 0.35 }}>
        {logic.rules.map((r, i) => (
          <Box component="li" key={i} sx={{ ...TYPE.meta, fontSize: 11.5, color: "rgba(255,255,255,.88)", lineHeight: 1.45 }}>
            {r}
          </Box>
        ))}
      </Box>
    )}
    {logic.source && (
      <Txt sx={{ ...TYPE.microMono, fontSize: 10.5, fontWeight: 400, color: "rgba(255,255,255,.6)", display: "block", mt: 0.9 }}>
        {logic.source}
      </Txt>
    )}
  </Box>
);

export const Item = ({ swatch, frameSx, label, hint, logic, dev }) => {
  const showLogic = dev && logic;
  // A plain Box, not Flx: MUI's Tooltip binds its hover listeners through a
  // ref on its child, and Flx doesn't forward refs — the tooltip would never
  // open (only the help cursor would show).
  const frame = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: FRAME_W,
        height: FRAME_H,
        flexShrink: 0,
        borderRadius: "5px",
        overflow: "hidden",
        background: "#fff",
        border: `1.5px solid ${FRAME_LINE}`,
        ...(showLogic && { cursor: "help" }),
        ...frameSx,
      }}
    >
      {swatch}
    </Box>
  );
  return (
    <Flx g={1.5} sx={{ minWidth: 0, alignItems: "center" }}>
      {showLogic ? (
        <Tooltip title={<LogicTip logic={logic} />} arrow placement="left" enterDelay={150}>
          {frame}
        </Tooltip>
      ) : (
        frame
      )}
      <Txt sx={{ ...TYPE.meta, color: slate[700], minWidth: 0, lineHeight: 1.5 }}>
        <Box component="span" sx={{ fontWeight: 600, color: slate[800], whiteSpace: "nowrap" }}>
          {label}
          {hint ? ":" : ""}
        </Box>
        {hint && (
          <Box component="span" sx={{ color: slate[500], fontSize: 11, ml: 0.75 }}>
            {hint}
          </Box>
        )}
      </Txt>
    </Flx>
  );
};

export const Group = ({ title, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Txt sx={{ ...TYPE.micro, letterSpacing: "0.07em", textTransform: "uppercase", color: slate[500], display: "block", mb: 1.25 }}>
      {title}
    </Txt>
    <Flx column g={1.25}>{children}</Flx>
  </Box>
);

// The readiness levels the chip colour encodes (fill + left bar).
const LEVEL_HINTS = {
  blocked: "a hard stop: hard exception, lock expiring, wire-block day",
  not_scheduled: "a required scheduling entry is missing",
  at_risk: "open items and funding is near, stale, or the date keeps moving",
  ready_with_items: "open items, nothing near-term or stale",
  ready: "nothing open that blocks the wire",
  closed: "funded / settled, shown on its funded date",
};

// Admin-only: the rule behind each level, as the backend evaluates it
// (closing_readiness.py `_level` / `_scheduling`; the level is on the card as
// `readinessLevel`, `closed` wins over it).
const LEVEL_LOGIC = {
  blocked: {
    when: "The loan is dead, or something hard-stops the wire.",
    rules: [
      "loanStatus is Dead (acquisitions: a dead / kicked trade or loan status)",
      "or any gating item is hard: an open HARD exception",
      "or the rate lock expires before the funding date",
      "or the funding date falls on a wire-block bank holiday",
    ],
    source: "readinessLevel = blocked · closing_readiness.py _level()",
  },
  not_scheduled: {
    when: "A required scheduling entry is still empty.",
    rules: [
      "preclose needs: closingScheduled, closingScheduledDate, expectedClosingDate, loanCloser (fundingType is optional)",
      "acquisition needs: tradeStatus, sellerCorrespondentName, purchasePriceAmount, plus settlementDate once closed / expectedClosingDate while open",
      "checked before anything else — docs, conditions and exceptions don't matter until these exist",
    ],
    source: "scheduling.missingRequired non-empty → readinessLevel = not_scheduled",
  },
  at_risk: {
    when: "Open funding-gating items AND a time or activity pressure.",
    rules: [
      "funding date within 3 days (or already passed)",
      "or stale: no activity on the loan for 7+ days",
      "or the funding date is after the closing cutoff",
      "or the scheduled date passed without closing",
      "or expected-date churn: 3+ ECD changes in the last 30 days",
      "also at risk with no open items when the date passed, churned, or was previously scheduled and missed",
    ],
    source: "readinessLevel = at_risk · closing_readiness.py _level()",
  },
  ready_with_items: {
    when: "Open funding-gating items, but nothing pressing.",
    rules: [
      "open prior-to-funding conditions and/or open (non-hard) exceptions",
      "none of the At-risk triggers: not near, not stale, not past cutoff, date not passed, no churn",
    ],
    source: "readinessLevel = ready_with_items",
  },
  ready: {
    when: "Nothing open that blocks the wire.",
    rules: [
      "no open funding-gating items (post-close conditions are allowed)",
      "green (fundingReady) only when also in a funding stage: preclose Clear To Close / Closing; acquisition trade Committed / Approved / Funded, or status Cleared to Purchase / Funding",
      "otherwise the reasons list says \"Not yet in a funding stage\"",
    ],
    source: "readinessLevel = ready · fundingReady = ready && funding stage",
  },
  closed: {
    when: "The loan has funded / the trade has settled.",
    rules: [
      "preclose: loanStatus is Closed",
      "acquisition: loanStatus or tradeStatus is Settled",
      "plotted on closedDate: fundedDate → originationDate (acquisition: settlementDate → fundedDate → wireReleaseDate)",
      "excluded from every funding total; hidden unless Show closed is on",
    ],
    source: "closed = true wins over readinessLevel",
  },
};

// The legend body on its own — the calendars that keep the legend behind a
// button render this inside a popover. `stacked` lays the three groups out in
// one column (for a narrow popover) instead of the three-up grid.
export const ReadinessLegendContent = ({ extra, extraGroups = [], stacked = false, dev = false, sx }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: stacked ? "1fr" : { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
      columnGap: 3,
      rowGap: stacked ? 0 : 2,
      ...(stacked && {
        "& > * + *": { borderTop: `1px solid ${slate[100]}`, pt: 1.75, mt: 1.75 },
      }),
      ...sx,
    }}
  >
    {/* The level lives on the EDGE BAR. The fill is a different axis (below) —
        most chips are white because most loans only have an expected date. */}
    <Group title="Edge bar · readiness">
      {READINESS_ORDER.filter((k) => k !== "closed").map((k) => (
        <Item
          key={k}
          swatch={<BarSwatch c={READINESS[k].c} />}
          label={READINESS[k].label}
          hint={LEVEL_HINTS[k]}
          logic={LEVEL_LOGIC[k]}
          dev={dev}
        />
      ))}
    </Group>

    <Group title="Fill · date">
      <Item
        swatch={null}
        frameSx={FILL_SX.firm}
        label="Tinted · solid"
        hint="firm date (scheduled, settlement, or funded); tint matches the bar"
        logic={{
          when: "A firm date exists for the event.",
          rules: [
            "preclose: closingScheduledDate is set → dateBasis = scheduled",
            "acquisition: settlementDate is set → dateBasis = settlement",
            "closed loans are always firm → dateBasis = funded",
            "the tint is the level colour at 11%",
          ],
          source: "dateBasis ∈ {scheduled, settlement, funded}",
        }}
        dev={dev}
      />
      <Item
        swatch={null}
        frameSx={FILL_SX.soft}
        label="White · grey dashed · est."
        hint="expected date only, nothing scheduled yet"
        logic={{
          when: "Only an expected date exists — nothing has been scheduled.",
          rules: [
            "expectedClosingDate is set, the firm date field is empty",
            "the chip sits on the expected date as a soft placement",
          ],
          source: "dateBasis = expected · FE isSoftDate(card)",
        }}
        dev={dev}
      />
      <Item
        swatch={<Mark sx={{ textDecoration: "line-through", color: slate[300], fontSize: 8.5 }}>#0000</Mark>}
        frameSx={FILL_SX.closed}
        label="Grey · struck through · closed"
        hint="funded or settled; shown on its funded date, excluded from totals"
        logic={{
          when: "The loan has funded / the trade has settled.",
          rules: [
            "preclose: loanStatus is Closed",
            "acquisition: loanStatus or tradeStatus is Settled",
            "plotted on closedDate: fundedDate → originationDate (acquisition: settlementDate → fundedDate → wireReleaseDate)",
            "excluded from every funding total; hidden unless Show closed is on",
          ],
          source: "closed = true wins over readinessLevel",
        }}
        dev={dev}
      />
    </Group>

    {/* One group for everything the chip carries besides colour and border:
        the flag dots and the text marks. The dense chip prints `TR` / `z`,
        the full chip spells them out (`trade` on the second line, an amber
        dot) — same meaning, one row each. */}
    <Group title="Flags · on the chip">
      <Item
        swatch={<Mark>TR</Mark>}
        label="TR · trade"
        hint="an acquisition, a trade settlement rather than a closing"
        logic={{
          when: "The card is an acquisition, so the event is a trade settlement.",
          rules: ["mode is trade, or pipeline is acquisition"],
          source: "FE isTrade(card)",
        }}
        dev={dev}
      />
      <Item
        swatch={<Dot c={status.error.fg} />}
        label="Red dot"
        hint="one dot for any of: date passed, missed before, wire-blocked, past cutoff, lock expires before funding"
        logic={{
          when: "Any hard red flag is set on the card.",
          rules: [
            "scheduledDatePassed — the scheduled date came and went",
            "missedScheduledDates — scheduled before and did not close",
            "wireBlocked — funding date is a wire-block holiday",
            "cutoffExceeded — funding date is after closingCutoffDate",
            "lockExpiresBeforeFunding — the rate lock runs out first",
          ],
          source: "FE cardAttentionChips(card), red entries",
        }}
        dev={dev}
      />
      <Item
        swatch={
          <Flx ac g={0.5}>
            <Dot c={amber.chipText} />
            <Mark sx={{ fontSize: 11, color: amber.dark }}>z</Mark>
          </Flx>
        }
        label="z · amber dot"
        hint="z = stale (no activity ≥ 7 days); amber dot = expected-date churn"
        logic={{
          when: "An activity or date-churn flag is set.",
          rules: [
            "z — stale: daysSinceActivity ≥ 7, measured from lastActivity.mostRecent",
            "amber dot — ecdChurn: expectedClosingDate changed 3+ times in the last 30 days",
          ],
          source: "FE cardAttentionChips(card), amber entries",
        }}
        dev={dev}
      />
      <Item
        swatch={<Mark>EV…</Mark>}
        label="EV number"
        hint="the loan's sync id, after the loan number"
        logic={{
          when: "The card carries a sync id.",
          rules: ["shown after the loan number; shrinks first when the cell is narrow"],
          source: "card.syncLoanId",
        }}
        dev={dev}
      />
      <Item
        swatch={<Mark>n · $</Mark>}
        label="Day header"
        hint="loans on that day · their total amount"
        logic={{
          when: "The day has at least one pending funding.",
          rules: [
            "count = cards on the day with closed = false",
            "amount = sum of loanAmount over those cards",
            "a card's day is closedDate when closed, otherwise fundingDate (scheduled → settlement → expected)",
          ],
          source: "FE: day cell header",
        }}
        dev={dev}
      />
      {extra}
    </Group>

    {extraGroups.map((g) => (
      <Group key={g.title} title={g.title}>
        {g.items.map((it) => (
          <Item key={it.key || it.label} swatch={it.swatch} frameSx={it.frameSx} label={it.label} hint={it.hint} logic={it.logic} dev={dev} />
        ))}
      </Group>
    ))}
  </Box>
);

const ReadinessLegend = ({ defaultOpen = false, extra }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { isAdmin } = usePermissionsHook();
  return (
    <Box sx={{ mt: 1.5 }}>
      <Flx
        ac
        g={0.6}
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: "pointer", userSelect: "none", width: "fit-content" }}
      >
        <InfoOutlined sx={{ fontSize: 14, color: slate[400] }} />
        <Txt sx={{ ...TYPE.toggle, color: slate[500] }}>What the chips mean</Txt>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: 16,
            color: slate[400],
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      {open && (
        <ReadinessLegendContent
          extra={extra}
          dev={isAdmin}
          sx={{
            mt: 1,
            p: 2,
            borderRadius: "12px",
            border: `1px solid ${slate[200]}`,
            background: "#fff",
          }}
        />
      )}
    </Box>
  );
};

export default ReadinessLegend;
